<template>
  <div class="content">
    <div class="container-fluid">
      <!-- Header -->
      <div class="row">
        <div class="col-12">
          <div class="card trainer-schedule-header">
            <div class="card-header trainer-card-header">
              <div class="row align-items-center">
                <div class="col-sm-6">
                  <h4 class="card-title trainer-title">
                    <i class="nc-icon nc-calendar-60"></i>
                    Agenda y Calendario
                  </h4>
                  <p class="card-category trainer-subtitle">
                    Gestiona tus sesiones y horarios
                  </p>
                </div>
                <div class="col-sm-6 text-right">
                  <button class="btn btn-light trainer-btn-new" @click="showNewSessionModal = true">
                    <i class="nc-icon nc-simple-add"></i>
                    Nueva Sesión
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Vista Calendario -->
      <div class="row">
        <div class="col-lg-8">
          <div class="card trainer-card">
            <div class="card-header">
              <div class="row align-items-center">
                <div class="col-md-6">
                  <h6 class="card-title">{{ currentMonthYear }}</h6>
                </div>
                <div class="col-md-6 text-right">
                  <div class="btn-group">
                    <button class="btn btn-sm btn-outline-primary" @click="previousMonth">
                      <i class="nc-icon nc-minimal-left"></i>
                    </button>
                    <button class="btn btn-sm btn-outline-primary" @click="today">Hoy</button>
                    <button class="btn btn-sm btn-outline-primary" @click="nextMonth">
                      <i class="nc-icon nc-minimal-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="card-body p-0">
              <div class="trainer-calendar">
                <div class="trainer-calendar-header">
                  <div class="trainer-day-header">Lun</div>
                  <div class="trainer-day-header">Mar</div>
                  <div class="trainer-day-header">Mié</div>
                  <div class="trainer-day-header">Jue</div>
                  <div class="trainer-day-header">Vie</div>
                  <div class="trainer-day-header">Sáb</div>
                  <div class="trainer-day-header">Dom</div>
                </div>
                <div class="trainer-calendar-body">
                  <div 
                    v-for="day in calendarDays" 
                    :key="day.date"
                    class="trainer-calendar-day"
                    :class="{
                      'other-month': !day.isCurrentMonth,
                      'today': day.isToday,
                      'has-sessions': day.sessions.length > 0
                    }"
                    @click="selectDay(day)"
                  >
                    <div class="trainer-day-number">{{ day.dayNumber }}</div>
                    <div class="trainer-day-sessions">
                      <div 
                        v-for="session in day.sessions.slice(0, 3)" 
                        :key="session.id"
                        class="trainer-session-dot"
                        :class="getSessionClass(session.type)"
                        :title="session.client + ' - ' + session.time"
                      ></div>
                      <div v-if="day.sessions.length > 3" class="trainer-more-sessions">
                        +{{ day.sessions.length - 3 }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Panel Lateral -->
        <div class="col-lg-4">
          <!-- Sesiones del Día -->
          <div class="card trainer-card">
            <div class="card-header">
              <h6 class="card-title">
                {{ selectedDay ? formatSelectedDay(selectedDay) : 'Sesiones de Hoy' }}
              </h6>
            </div>
            <div class="card-body">
              <div class="trainer-day-sessions-list">
                <div v-if="selectedDaySessions.length === 0" class="text-center py-4">
                  <i class="nc-icon nc-calendar-60" style="font-size: 32px; color: #ccc;"></i>
                  <p class="text-muted mt-2">Sin sesiones programadas</p>
                </div>
                <div v-else>
                  <div v-for="session in selectedDaySessions" :key="session.id" class="trainer-session-item">
                    <div class="trainer-session-time">
                      <strong>{{ session.time }}</strong>
                    </div>
                    <div class="trainer-session-info">
                      <h6>{{ session.client }}</h6>
                      <p>{{ session.type }}</p>
                      <div class="trainer-session-status">
                        <span class="badge" :class="getStatusBadgeClass(session.status)">
                          {{ getStatusLabel(session.status) }}
                        </span>
                      </div>
                    </div>
                    <div class="trainer-session-actions">
                      <button class="btn btn-sm btn-outline-primary" @click="editSession(session)">
                        <i class="nc-icon nc-settings-gear-65"></i>
                      </button>
                      <button v-if="session.status === 'programada'" class="btn btn-sm btn-outline-success" @click="startSession(session)">
                        <i class="nc-icon nc-button-play"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Estadísticas Semanales -->
          <div class="card trainer-card">
            <div class="card-header">
              <h6 class="card-title">Esta Semana</h6>
            </div>
            <div class="card-body">
              <div class="trainer-week-stats">
                <div class="trainer-stat-row">
                  <span>Sesiones programadas</span>
                  <strong>{{ weekStats.scheduled }}</strong>
                </div>
                <div class="trainer-stat-row">
                  <span>Completadas</span>
                  <strong class="text-success">{{ weekStats.completed }}</strong>
                </div>
                <div class="trainer-stat-row">
                  <span>Canceladas</span>
                  <strong class="text-danger">{{ weekStats.cancelled }}</strong>
                </div>
                <div class="trainer-stat-row">
                  <span>Ingresos estimados</span>
                  <strong class="text-primary">${{ weekStats.revenue.toLocaleString() }}</strong>
                </div>
              </div>
            </div>
          </div>

          <!-- Disponibilidad Rápida -->
          <div class="card trainer-card">
            <div class="card-header">
              <h6 class="card-title">Próximos Espacios Libres</h6>
            </div>
            <div class="card-body">
              <div class="trainer-available-slots">
                <div v-for="slot in availableSlots" :key="slot.id" class="trainer-slot-item">
                  <div class="trainer-slot-time">
                    <strong>{{ slot.day }}</strong>
                    <br><small>{{ slot.time }}</small>
                  </div>
                  <div class="trainer-slot-actions">
                    <button class="btn btn-sm trainer-btn-primary" @click="bookSlot(slot)">
                      Reservar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Lista de Próximas Sesiones -->
      <div class="row">
        <div class="col-12">
          <div class="card trainer-card">
            <div class="card-header">
              <h6 class="card-title">Próximas Sesiones (7 días)</h6>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table trainer-sessions-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Hora</th>
                      <th>Cliente</th>
                      <th>Tipo</th>
                      <th>Estado</th>
                      <th>Notas</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="session in upcomingSessions" :key="session.id">
                      <td>{{ formatDate(session.date) }}</td>
                      <td><strong>{{ session.time }}</strong></td>
                      <td>
                        <div class="trainer-client-info">
                          <img :src="session.clientAvatar" :alt="session.client" class="trainer-client-avatar">
                          <span>{{ session.client }}</span>
                        </div>
                      </td>
                      <td>
                        <span class="badge trainer-type-badge" :class="getTypeBadgeClass(session.type)">
                          {{ session.type }}
                        </span>
                      </td>
                      <td>
                        <span class="badge" :class="getStatusBadgeClass(session.status)">
                          {{ getStatusLabel(session.status) }}
                        </span>
                      </td>
                      <td>
                        <small class="text-muted">{{ session.notes || '-' }}</small>
                      </td>
                      <td>
                        <div class="trainer-actions">
                          <button class="btn btn-sm btn-outline-info" @click="viewSession(session)">
                            <i class="nc-icon nc-zoom-split"></i>
                          </button>
                          <button class="btn btn-sm btn-outline-primary" @click="editSession(session)">
                            <i class="nc-icon nc-settings-gear-65"></i>
                          </button>
                          <button class="btn btn-sm btn-outline-danger" @click="cancelSession(session)">
                            <i class="nc-icon nc-simple-remove"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Nueva Sesión -->
    <div class="modal" v-if="showNewSessionModal" @click="showNewSessionModal = false">
      <div class="modal-dialog" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Nueva Sesión</h5>
            <button type="button" class="close" @click="showNewSessionModal = false">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <form>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Cliente</label>
                    <select class="form-control">
                      <option value="">Seleccionar cliente</option>
                      <option>Ana Martínez</option>
                      <option>Carlos Ruiz</option>
                      <option>María González</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Tipo de Sesión</label>
                    <select class="form-control">
                      <option>Entrenamiento Personal</option>
                      <option>Evaluación</option>
                      <option>Consultoría</option>
                      <option>Seguimiento</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Fecha</label>
                    <input type="date" class="form-control">
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Hora</label>
                    <input type="time" class="form-control">
                  </div>
                </div>
              </div>
              <div class="form-group">
                <label>Duración (minutos)</label>
                <select class="form-control">
                  <option value="30">30 minutos</option>
                  <option value="45">45 minutos</option>
                  <option value="60" selected>60 minutos</option>
                  <option value="90">90 minutos</option>
                </select>
              </div>
              <div class="form-group">
                <label>Notas</label>
                <textarea class="form-control" rows="3" placeholder="Notas sobre la sesión..."></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showNewSessionModal = false">Cancelar</button>
            <button type="button" class="btn trainer-btn-primary" @click="createSession">Programar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Schedule',
  data() {
    return {
      showNewSessionModal: false,
      currentDate: new Date(),
      selectedDay: null,
      weekStats: {
        scheduled: 28,
        completed: 24,
        cancelled: 2,
        revenue: 1080000
      },
      availableSlots: [
        { id: 1, day: 'Mañana', time: '09:00', date: '2024-01-20' },
        { id: 2, day: 'Hoy', time: '15:00', date: '2024-01-19' },
        { id: 3, day: 'Lunes', time: '11:30', date: '2024-01-22' }
      ],
      sessions: [
        {
          id: 1,
          date: '2024-01-19',
          time: '09:00',
          client: 'Ana Martínez',
          clientAvatar: '/static/img/faces/ayo-ogunseinde-2.jpg',
          type: 'Entrenamiento',
          status: 'programada',
          notes: 'Enfoque en piernas'
        },
        {
          id: 2,
          date: '2024-01-19',
          time: '11:00',
          client: 'Carlos Ruiz',
          clientAvatar: '/static/img/faces/erik-lucatero-2.jpg',
          type: 'Evaluación',
          status: 'completada',
          notes: 'Test de fuerza mensual'
        },
        {
          id: 3,
          date: '2024-01-20',
          time: '10:00',
          client: 'María González',
          clientAvatar: '/static/img/faces/avatar.png',
          type: 'Consultoría',
          status: 'programada',
          notes: 'Revisión de objetivos'
        }
      ]
    }
  },
  computed: {
    currentMonthYear() {
      return this.currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    },
    calendarDays() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay() + 1);
      
      const days = [];
      const today = new Date();
      
      for (let i = 0; i < 42; i++) {
        const currentDay = new Date(startDate);
        currentDay.setDate(startDate.getDate() + i);
        
        const dateString = currentDay.toISOString().split('T')[0];
        const daySessions = this.sessions.filter(session => session.date === dateString);
        
        days.push({
          date: dateString,
          dayNumber: currentDay.getDate(),
          isCurrentMonth: currentDay.getMonth() === month,
          isToday: dateString === today.toISOString().split('T')[0],
          sessions: daySessions
        });
      }
      
      return days;
    },
    selectedDaySessions() {
      if (!this.selectedDay) {
        const today = new Date().toISOString().split('T')[0];
        return this.sessions.filter(session => session.date === today);
      }
      return this.sessions.filter(session => session.date === this.selectedDay.date);
    },
    upcomingSessions() {
      const today = new Date();
      const nextWeek = new Date();
      nextWeek.setDate(today.getDate() + 7);
      
      return this.sessions.filter(session => {
        const sessionDate = new Date(session.date);
        return sessionDate >= today && sessionDate <= nextWeek;
      }).sort((a, b) => {
        if (a.date === b.date) {
          return a.time.localeCompare(b.time);
        }
        return a.date.localeCompare(b.date);
      });
    }
  },
  methods: {
    previousMonth() {
      this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
    },
    nextMonth() {
      this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
    },
    today() {
      this.currentDate = new Date();
      this.selectedDay = null;
    },
    selectDay(day) {
      this.selectedDay = day;
    },
    formatSelectedDay(day) {
      const date = new Date(day.date);
      return date.toLocaleDateString('es-ES', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
      });
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES');
    },
    getSessionClass(type) {
      const classes = {
        'Entrenamiento': 'session-training',
        'Evaluación': 'session-evaluation',
        'Consultoría': 'session-consultation',
        'Seguimiento': 'session-followup'
      };
      return classes[type] || 'session-default';
    },
    getStatusBadgeClass(status) {
      const classes = {
        'programada': 'badge-info',
        'completada': 'badge-success',
        'cancelada': 'badge-danger',
        'en_progreso': 'badge-warning'
      };
      return classes[status] || 'badge-secondary';
    },
    getTypeBadgeClass(type) {
      const classes = {
        'Entrenamiento': 'badge-primary',
        'Evaluación': 'badge-warning',
        'Consultoría': 'badge-info',
        'Seguimiento': 'badge-success'
      };
      return classes[type] || 'badge-secondary';
    },
    getStatusLabel(status) {
      const labels = {
        'programada': 'Programada',
        'completada': 'Completada',
        'cancelada': 'Cancelada',
        'en_progreso': 'En Progreso'
      };
      return labels[status] || status;
    },
    editSession(session) {
      this.$swal.fire({
        title: 'Editar Sesión',
        text: `Editar sesión con ${session.client}`,
        confirmButtonColor: '#9ACD32'
      });
    },
    startSession(session) {
      this.$swal.fire({
        title: 'Iniciar Sesión',
        text: `Iniciar sesión con ${session.client}`,
        confirmButtonColor: '#9ACD32'
      });
    },
    viewSession(session) {
      this.$swal.fire({
        title: 'Detalles de Sesión',
        text: `Ver detalles de la sesión con ${session.client}`,
        confirmButtonColor: '#9ACD32'
      });
    },
    cancelSession(session) {
      this.$swal.fire({
        title: '¿Cancelar sesión?',
        text: `¿Estás seguro de cancelar la sesión con ${session.client}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#9ACD32',
        confirmButtonText: 'Cancelar Sesión',
        cancelButtonText: 'Mantener'
      });
    },
    bookSlot(slot) {
      this.$swal.fire({
        title: 'Reservar Espacio',
        text: `Reservar espacio del ${slot.day} a las ${slot.time}`,
        confirmButtonColor: '#9ACD32'
      });
    },
    createSession() {
      this.showNewSessionModal = false;
      this.$swal.fire({
        title: 'Sesión Programada',
        text: 'La sesión ha sido programada exitosamente',
        icon: 'success',
        confirmButtonColor: '#9ACD32'
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.content {
  --trainer-primary: #9ACD32;
  --trainer-secondary: #7CB342;
  --trainer-accent: #8BC34A;
  --trainer-dark: #689F38;
  --trainer-light: #DCEDC8;
  --trainer-bg: #F1F8E9;
}

.trainer-schedule-header {
  background: linear-gradient(135deg, var(--trainer-primary), var(--trainer-secondary));
  color: white;
  border: none;
  box-shadow: 0 4px 20px 0px rgba(154, 205, 50, 0.14), 0 7px 10px -5px rgba(154, 205, 50, 0.4);
  
  .trainer-card-header {
    background: transparent;
    border: none;
  }
  
  .trainer-title {
    color: white;
    font-weight: 500;
    margin-bottom: 5px;
    
    i {
      margin-right: 10px;
    }
  }
  
  .trainer-subtitle {
    color: rgba(255,255,255,0.8);
    margin: 0;
  }
  
  .trainer-btn-new {
    border: 2px solid rgba(255,255,255,0.3);
    color: white;
    
    &:hover {
      background: rgba(255,255,255,0.1);
      border-color: rgba(255,255,255,0.5);
    }
  }
}

.trainer-card {
  border: none;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
  margin-bottom: 20px;
  
  .card-header {
    background: var(--trainer-bg);
    border-bottom: 1px solid #dee2e6;
    
    .card-title {
      color: var(--trainer-dark);
      font-weight: 600;
      margin: 0;
    }
  }
}

.trainer-calendar {
  .trainer-calendar-header {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    background: var(--trainer-bg);
    
    .trainer-day-header {
      padding: 12px;
      text-align: center;
      font-weight: 600;
      color: var(--trainer-dark);
      border-right: 1px solid #e9ecef;
      
      &:last-child {
        border-right: none;
      }
    }
  }
  
  .trainer-calendar-body {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    
    .trainer-calendar-day {
      min-height: 100px;
      border: 1px solid #e9ecef;
      padding: 8px;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        background: var(--trainer-bg);
      }
      
      &.other-month {
        color: #ccc;
        background: #f8f9fa;
      }
      
      &.today {
        background: rgba(154, 205, 50, 0.1);
        border-color: var(--trainer-primary);
        
        .trainer-day-number {
          background: var(--trainer-primary);
          color: white;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
        }
      }
      
      &.has-sessions {
        background: rgba(154, 205, 50, 0.05);
      }
      
      .trainer-day-number {
        font-weight: 500;
        margin-bottom: 5px;
      }
      
      .trainer-day-sessions {
        display: flex;
        flex-wrap: wrap;
        gap: 2px;
        
        .trainer-session-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          
          &.session-training {
            background: #007bff;
          }
          
          &.session-evaluation {
            background: #ffc107;
          }
          
          &.session-consultation {
            background: #17a2b8;
          }
          
          &.session-followup {
            background: #28a745;
          }
        }
        
        .trainer-more-sessions {
          font-size: 10px;
          color: #666;
          margin-left: 2px;
        }
      }
    }
  }
}

.trainer-day-sessions-list {
  .trainer-session-item {
    display: flex;
    align-items: center;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 6px;
    margin-bottom: 15px;
    
    .trainer-session-time {
      margin-right: 15px;
      text-align: center;
      min-width: 60px;
      
      strong {
        color: var(--trainer-primary);
        font-size: 16px;
      }
    }
    
    .trainer-session-info {
      flex: 1;
      
      h6 {
        margin: 0 0 5px 0;
        color: var(--trainer-dark);
        font-weight: 600;
      }
      
      p {
        margin: 0 0 8px 0;
        color: #666;
        font-size: 14px;
      }
    }
    
    .trainer-session-actions {
      display: flex;
      gap: 5px;
      
      .btn {
        padding: 4px 8px;
        font-size: 12px;
      }
    }
  }
}

.trainer-week-stats {
  .trainer-stat-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
    
    &:last-child {
      border-bottom: none;
      margin-bottom: 0;
    }
    
    span {
      color: #666;
    }
    
    strong {
      color: var(--trainer-dark);
    }
  }
}

.trainer-available-slots {
  .trainer-slot-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 6px;
    margin-bottom: 10px;
    
    .trainer-slot-time {
      strong {
        color: var(--trainer-dark);
      }
      
      small {
        color: #666;
      }
    }
  }
}

.trainer-sessions-table {
  .trainer-client-info {
    display: flex;
    align-items: center;
    
    .trainer-client-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      margin-right: 8px;
      object-fit: cover;
    }
  }
  
  .trainer-type-badge {
    font-size: 11px;
    padding: 4px 8px;
  }
  
  .trainer-actions {
    display: flex;
    gap: 5px;
    
    .btn {
      padding: 4px 8px;
      font-size: 12px;
    }
  }
}

.trainer-btn-primary {
  background: var(--trainer-primary);
  border-color: var(--trainer-primary);
  color: white;
  
  &:hover {
    background: var(--trainer-secondary);
    border-color: var(--trainer-secondary);
  }
}

.form-control {
  border: 2px solid #e9ecef;
  border-radius: 6px;
  
  &:focus {
    border-color: var(--trainer-primary);
    box-shadow: 0 0 0 0.2rem rgba(154, 205, 50, 0.25);
  }
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  
  .modal-dialog {
    background: white;
    border-radius: 8px;
    max-width: 90%;
    max-height: 90%;
    overflow-y: auto;
  }
  
  .modal-header {
    padding: 20px;
    border-bottom: 1px solid #dee2e6;
    
    .modal-title {
      color: var(--trainer-dark);
      font-weight: 600;
    }
    
    .close {
      background: none;
      border: none;
      font-size: 24px;
      line-height: 1;
      color: #999;
      cursor: pointer;
    }
  }
  
  .modal-body {
    padding: 20px;
  }
  
  .modal-footer {
    padding: 20px;
    border-top: 1px solid #dee2e6;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
}
</style>